import { initializeDi } from "./di/registry";
initializeDi();

// Connect router to auth state after DI init
import { router, navigateTo } from "./router";
import { connectRouterToAuth, waitForAuthReady } from "ui-common/router/router-auth-connector";
import { resolve } from "ui-common/di";
import type { ICreatorLiveEventsService } from "./services/creator-live-events/types";
import type { ILiveEventFormService } from "ui-common/services/live-event-form/types";
import type { INotificationsService } from "ui-common/services/notifications/types";
import type { ICreatorProfileDataService } from "./services/creator-profile-data/types";
import type { IBackofficeFeatureFlagsService } from "./services/backoffice-feature-flags/types";
import type { ISubscriptionTierService } from "./services/subscription-tier/types";
import type { IBackofficePostsService } from "./services/backoffice-posts/types";
import type { IBackofficeWorldsService } from "./services/backoffice-worlds/types";
import type { IBackofficeLinksService } from "./services/backoffice-links/types";
import type { ILiveEventAccessRequestService } from "./services/live-event-access-request/types";
import type { ICreatorMessagesService } from "./services/creator-messages/types";

connectRouterToAuth(router, navigateTo, {
  postSignInRoute: "profile",
  signInRoute: "sign-in",
  onUserChange: (user) => {
    const liveEventsService = resolve<ICreatorLiveEventsService>("creatorLiveEventsService");
    const liveEventFormService = resolve<ILiveEventFormService>("liveEventFormService");

    if (user) {
      const backofficeFeatureFlagsService = resolve<IBackofficeFeatureFlagsService>("backofficeFeatureFlagsService");
      backofficeFeatureFlagsService.loadFlags();

      const creatorProfileDataService = resolve<ICreatorProfileDataService>("creatorProfileDataService");
      creatorProfileDataService.loadProfile();

      const notificationsService = resolve<INotificationsService>("notificationsService");
      notificationsService.loadNotifications();

      const subscriptionTierService = resolve<ISubscriptionTierService>("subscriptionTierService");
      subscriptionTierService.loadTier();

      if (user.roles.includes("creator")) {
        const liveEventAccessRequestService = resolve<ILiveEventAccessRequestService>("liveEventAccessRequestService");
        liveEventAccessRequestService.loadRequestStatus();

        liveEventsService.loadEvents();
        liveEventFormService.initialize();

        const backofficePostsService = resolve<IBackofficePostsService>("backofficePostsService");
        backofficePostsService.loadPosts();

        const backofficeWorldsService = resolve<IBackofficeWorldsService>("backofficeWorldsService");
        backofficeWorldsService.loadWorlds();

        const backofficeLinksService = resolve<IBackofficeLinksService>("backofficeLinksService");
        backofficeLinksService.loadLinks();

        const creatorMessagesService = resolve<ICreatorMessagesService>("creatorMessagesService");
        creatorMessagesService.loadMessages();
      }
    } else {
      liveEventsService.clearCache();
    }
  },
});

// Wait for initial auth check before rendering
await waitForAuthReady();

import { LitElement, css, html } from "lit";

import { customElement } from "lit/decorators.js";

import "@phosphor-icons/webcomponents/PhBeerStein";

import "@phosphor-icons/webcomponents/PhCamera";
import "@phosphor-icons/webcomponents/PhCaretRight";
import "@phosphor-icons/webcomponents/PhCaretLeft";
import "@phosphor-icons/webcomponents/PhChartBar";
import "@phosphor-icons/webcomponents/PhCheck";
import "@phosphor-icons/webcomponents/PhCheckCircle";
import "@phosphor-icons/webcomponents/PhCircleNotch";
import "@phosphor-icons/webcomponents/PhDiscordLogo";
import "@phosphor-icons/webcomponents/PhDotsThree";
import "@phosphor-icons/webcomponents/PhEye";
import "@phosphor-icons/webcomponents/PhEyedropper";
import "@phosphor-icons/webcomponents/PhFacebookLogo";
import "@phosphor-icons/webcomponents/PhGear";
import "@phosphor-icons/webcomponents/PhImage";
import "@phosphor-icons/webcomponents/PhImages";
import "@phosphor-icons/webcomponents/PhInstagramLogo";
import "@phosphor-icons/webcomponents/PhLink";
import "@phosphor-icons/webcomponents/PhLinkedinLogo";
import "@phosphor-icons/webcomponents/PhMoon";
import "@phosphor-icons/webcomponents/PhPencilLine";
import "@phosphor-icons/webcomponents/PhPinterestLogo";
import "@phosphor-icons/webcomponents/PhPlus";
import "@phosphor-icons/webcomponents/PhPlusCircle";
import "@phosphor-icons/webcomponents/PhQuestion";
import "@phosphor-icons/webcomponents/PhSealPercent";
import "@phosphor-icons/webcomponents/PhSignOut";
import "@phosphor-icons/webcomponents/PhSnapchatLogo";
import "@phosphor-icons/webcomponents/PhSoundcloudLogo";
import "@phosphor-icons/webcomponents/PhSpinner";
import "@phosphor-icons/webcomponents/PhSpotifyLogo";
import "@phosphor-icons/webcomponents/PhSun";
import "@phosphor-icons/webcomponents/PhTiktokLogo";
import "@phosphor-icons/webcomponents/PhTrashSimple";
import "@phosphor-icons/webcomponents/PhTwitchLogo";
import "@phosphor-icons/webcomponents/PhUser";
import "@phosphor-icons/webcomponents/PhWarning";
import "@phosphor-icons/webcomponents/PhWarningCircle";
import "@phosphor-icons/webcomponents/PhXCircle";
import "@phosphor-icons/webcomponents/PhXLogo";
import "@phosphor-icons/webcomponents/PhYoutubeLogo";
import "@phosphor-icons/webcomponents/PhEnvelope";
import "@phosphor-icons/webcomponents/PhCrown";
import "@phosphor-icons/webcomponents/PhSpeakerHigh";
import "@phosphor-icons/webcomponents/PhSpeakerSlash";

import "ui-common/theme";
import "ui-components/menu-button";
import "ui-components/notification/notification-manager";

import "./components/app-logo/app-logo";
import "./components/app-menu/app-menu";

import { initTelemetry } from "./telemetry";

import { isProduction } from "../../api/helpers/isProduction";

declare global {
  interface Window {
    dataLayer?: unknown[];
  }
}

requestAnimationFrame(() => {
  if (isProduction()) {
    (function (w: Window & typeof globalThis, d: Document, s: string, l: string, i: string) {
      w.dataLayer = [];
      w.dataLayer.push({ "gtm.start": new Date().getTime(), event: "gtm.js" });
      const f = d.getElementsByTagName(s)[0];
      const j = d.createElement(s) as HTMLScriptElement;
      const dl = l != "dataLayer" ? "&l=" + l : "";
      j.async = true;
      j.defer = true;
      j.src = "https://www.googletagmanager.com/gtm.js?id=" + i + dl;
      f?.parentNode?.insertBefore(j, f);
    })(window, document, "script", "dataLayer", "GTM-PXCZQLNV");
  }
  initTelemetry();
});

@customElement("wow-app")
export class App extends LitElement {
  static override styles = css`
    :host {
      display: block;
      min-height: 100dvh;
      background: var(--sds-color-background-default-default);
      transition: background-color 0.2s ease;
    }
  `;

  protected override render() {
    return html`
      ${router}
      <notification-manager></notification-manager>
    `;
  }
}
